import { jsPDF } from 'jspdf';
import QRCode from 'qrcode';
import { SPACE_GROTESK_TTF_BASE64 } from './space-grotesk-font';
const BRAND_FONT = 'SpaceGrotesk';
const COLORS = {
    brand: [16, 99, 184],
    brandSoft: [232, 242, 252],
    emerald: [17, 148, 112],
    emeraldSoft: [232, 248, 242],
    cyanSoft: [236, 248, 251],
    slate: [15, 23, 42],
    text: [30, 41, 59],
    muted: [100, 116, 139],
    border: [226, 232, 240],
    paper: [255, 255, 255],
    panel: [248, 250, 252],
    critical: [220, 38, 38],
    criticalSoft: [254, 242, 242],
    high: [234, 88, 12],
    highSoft: [255, 247, 237],
    medium: [217, 119, 6],
    mediumSoft: [255, 251, 235],
    low: [22, 163, 74],
    lowSoft: [240, 253, 244],
    info: [8, 145, 178],
    infoSoft: [236, 254, 255],
};
const PDF_BRAND_COLORS = {
    snap: COLORS.slate,
    v: COLORS.emerald,
    ai: COLORS.brand,
};
const getSeverityColor = (severity) => {
    switch (severity) {
        case 'CRITICAL':
            return COLORS.critical;
        case 'HIGH':
            return COLORS.high;
        case 'MEDIUM':
            return COLORS.medium;
        case 'LOW':
            return COLORS.low;
        default:
            return COLORS.info;
    }
};
const getSeveritySoftColor = (severity) => {
    switch (severity) {
        case 'CRITICAL':
            return COLORS.criticalSoft;
        case 'HIGH':
            return COLORS.highSoft;
        case 'MEDIUM':
            return COLORS.mediumSoft;
        case 'LOW':
            return COLORS.lowSoft;
        default:
            return COLORS.infoSoft;
    }
};
const formatDate = (value) => value.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
});
const formatDateTime = (value) => value.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
});
const drawRoundedCard = (pdf, x, y, width, height, fillColor, borderColor, radius = 4) => {
    if (fillColor) {
        pdf.setFillColor(fillColor[0], fillColor[1], fillColor[2]);
    }
    if (borderColor) {
        pdf.setDrawColor(borderColor[0], borderColor[1], borderColor[2]);
        pdf.setLineWidth(0.35);
    }
    pdf.roundedRect(x, y, width, height, radius, radius, fillColor && borderColor ? 'FD' : fillColor ? 'F' : 'S');
};
const drawMetricCard = (pdf, x, y, width, height, label, value, footnote, valueColor = COLORS.slate) => {
    drawRoundedCard(pdf, x, y, width, height, COLORS.paper, COLORS.border);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text(label.toUpperCase(), x + 4, y + 6);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(16);
    pdf.setTextColor(valueColor[0], valueColor[1], valueColor[2]);
    pdf.text(value, x + 4, y + 14);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text(footnote, x + 4, y + height - 4);
};
const drawProgressBar = (pdf, x, y, width, height, percentage, color) => {
    drawRoundedCard(pdf, x, y, width, height, [241, 245, 249], [226, 232, 240], 2);
    pdf.setFillColor(color[0], color[1], color[2]);
    pdf.roundedRect(x, y, Math.max(0, Math.min(width, (width * percentage) / 100)), height, 2, 2, 'F');
};
const drawSectionHeading = (pdf, x, y, eyebrow, title, subtitle) => {
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.brand[0], COLORS.brand[1], COLORS.brand[2]);
    pdf.text(eyebrow.toUpperCase(), x, y);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(17);
    pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
    pdf.text(title, x, y + 7);
    if (subtitle) {
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setFontSize(8.5);
        pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
        const lines = pdf.splitTextToSize(subtitle, 180);
        pdf.text(lines, x, y + 12);
    }
};
const drawBrandWordmarkPDF = (pdf, x, y, fontSize, options = {}) => {
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(fontSize);
    pdf.setTextColor(PDF_BRAND_COLORS.snap[0], PDF_BRAND_COLORS.snap[1], PDF_BRAND_COLORS.snap[2]);
    pdf.text('SNAP', x, y);
    const snapWidth = pdf.getTextWidth('SNAP');
    pdf.setTextColor(PDF_BRAND_COLORS.v[0], PDF_BRAND_COLORS.v[1], PDF_BRAND_COLORS.v[2]);
    pdf.text('V', x + snapWidth, y);
    const vWidth = pdf.getTextWidth('V');
    pdf.setTextColor(PDF_BRAND_COLORS.ai[0], PDF_BRAND_COLORS.ai[1], PDF_BRAND_COLORS.ai[2]);
    pdf.text('.AI', x + snapWidth + vWidth, y);
    const aiWidth = pdf.getTextWidth('.AI');
    const totalWidth = snapWidth + vWidth + aiWidth;
    if (options.suffix) {
        pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
        pdf.text(options.suffix, x + totalWidth + 3, y);
    }
    if (options.tagline) {
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setFontSize(Math.max(7, fontSize * 0.42));
        pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
        pdf.text('Trust Every AI Release', x + totalWidth + 4, y);
    }
    return totalWidth;
};
export async function generateProfessionalPDF(scan, ownerEmail, ownerCompanyName = null, validateUrl, isGuest = false, validationToken = null) {
    const pdf = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    pdf.addFileToVFS('SpaceGrotesk-Variable.ttf', SPACE_GROTESK_TTF_BASE64);
    pdf.addFont('SpaceGrotesk-Variable.ttf', BRAND_FONT, 'normal');
    pdf.addFont('SpaceGrotesk-Variable.ttf', BRAND_FONT, 'bold');
    pdf.setFont(BRAND_FONT);
    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const margin = 14;
    const contentWidth = pageWidth - margin * 2;
    const findingsBySeverity = {
        critical: scan.findings.filter((f) => f.severity === 'CRITICAL').length,
        high: scan.findings.filter((f) => f.severity === 'HIGH').length,
        medium: scan.findings.filter((f) => f.severity === 'MEDIUM').length,
        low: scan.findings.filter((f) => f.severity === 'LOW').length,
        info: scan.findings.filter((f) => f.severity === 'INFO').length,
    };
    const primaryAction = findingsBySeverity.critical > 0
        ? 'Hold release and remediate critical exposure before deployment.'
        : findingsBySeverity.high > 0
            ? 'Address high-severity findings before customer-facing rollout.'
            : scan.findings.length > 0
                ? 'Resolve remaining findings to move this asset toward a zero-vulnerability release state.'
                : 'No findings were detected in this scan. Preserve this report as release evidence.';
    const releasePositioning = scan.findings.length === 0
        ? 'Ready for evidence-backed signoff'
        : `${scan.findings.length} findings require review before signoff`;
    const reportBadgeLabel = isGuest ? 'Guest Copy' : 'Verified Copy';
    const reportBadgeFill = isGuest ? COLORS.highSoft : COLORS.emeraldSoft;
    const reportBadgeText = isGuest ? COLORS.high : COLORS.emerald;
    const ownerIdentity = ownerEmail && ownerCompanyName
        ? `${ownerEmail} · ${ownerCompanyName}`
        : ownerEmail || ownerCompanyName || 'Workspace owner';
    let currentPage = 1;
    let yPosition = margin;
    let qrDataUrl = '';
    const addWatermark = () => {
        if (!isGuest)
            return;
        pdf.saveGraphicsState();
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(66);
        pdf.setTextColor(225, 229, 234);
        pdf.text('COPY', pageWidth / 2, pageHeight / 2, { align: 'center', angle: -35 });
        pdf.restoreGraphicsState();
    };
    const addPageChrome = () => {
        pdf.setFillColor(COLORS.paper[0], COLORS.paper[1], COLORS.paper[2]);
        pdf.rect(0, 0, pageWidth, pageHeight, 'F');
        pdf.setFillColor(COLORS.brandSoft[0], COLORS.brandSoft[1], COLORS.brandSoft[2]);
        pdf.rect(0, 0, pageWidth, 12, 'F');
        pdf.setFillColor(COLORS.emeraldSoft[0], COLORS.emeraldSoft[1], COLORS.emeraldSoft[2]);
        pdf.rect(pageWidth - 56, 0, 56, 12, 'F');
        drawBrandWordmarkPDF(pdf, margin, 7.5, 8, { suffix: 'SECURITY REPORT' });
        const footerY = pageHeight - 18;
        pdf.setDrawColor(COLORS.border[0], COLORS.border[1], COLORS.border[2]);
        pdf.setLineWidth(0.35);
        pdf.line(margin, footerY, pageWidth - margin, footerY);
        drawBrandWordmarkPDF(pdf, margin, footerY + 6, 7, { tagline: true });
        pdf.text(`Page ${currentPage}`, pageWidth / 2 - 5, footerY + 6);
        if (qrDataUrl) {
            const qrSize = 12;
            const qrX = pageWidth - margin - qrSize;
            const qrY = footerY - qrSize - 2;
            pdf.addImage(qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
            pdf.setFont(BRAND_FONT, 'bold');
            pdf.setFontSize(5.5);
            pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
            pdf.text('Fingerprint', qrX - 30, qrY + 3);
            pdf.setFont(BRAND_FONT, 'normal');
            pdf.setFontSize(5.5);
            const fpLines = pdf.splitTextToSize(scan.fileHashFormatted, 28);
            pdf.text(fpLines, qrX - 30, qrY + 6.5);
        }
        addWatermark();
    };
    const addNewPage = () => {
        pdf.addPage();
        currentPage += 1;
        addPageChrome();
        yPosition = margin + 8;
    };
    const checkPageSpace = (requiredSpace) => {
        if (yPosition + requiredSpace > pageHeight - 28) {
            addNewPage();
        }
    };
    try {
        const qrUrl = `${validateUrl}/validate/${scan.fileHash}${validationToken ? `?token=${encodeURIComponent(validationToken)}` : ''}`;
        qrDataUrl = await QRCode.toDataURL(qrUrl, {
            width: 100,
            margin: 1,
            color: { dark: '#0f172a', light: '#ffffff' },
        });
    }
    catch (e) {
        // QR code generation failed silently
    }
    addPageChrome();
    yPosition = 20;
    const heroPanelHeight = 64;
    const heroContentWidth = contentWidth - 82;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, heroPanelHeight, COLORS.panel, COLORS.border, 6);
    pdf.setFillColor(COLORS.brand[0], COLORS.brand[1], COLORS.brand[2]);
    pdf.roundedRect(margin, yPosition, contentWidth, 15, 6, 6, 'F');
    pdf.rect(margin, yPosition + 10, contentWidth, 5, 'F');
    drawBrandWordmarkPDF(pdf, margin + 6, yPosition + 9, 19);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(226, 232, 240);
    pdf.text('AI asset security report', margin + 6, yPosition + 14);
    drawRoundedCard(pdf, margin + contentWidth - 46, yPosition + 2.5, 40, 10.5, reportBadgeFill, reportBadgeText, 3);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8.3);
    pdf.setTextColor(reportBadgeText[0], reportBadgeText[1], reportBadgeText[2]);
    pdf.text(reportBadgeLabel.toUpperCase(), margin + contentWidth - 41.5, yPosition + 9.2);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(20);
    pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
    const heroTitleLines = pdf.splitTextToSize('Security report built for release decisions', heroContentWidth);
    pdf.text(heroTitleLines, margin + 6, yPosition + 27);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(8.4);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    const heroCopy = pdf.splitTextToSize('A premium audit summary for AI-native code, MCP servers, skills, prompts, and agent workflows. Use this document to assess risk quickly, coordinate fixes, and preserve release evidence.', heroContentWidth);
    const heroCopyY = yPosition + 31 + heroTitleLines.length * 4.9;
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(9.6);
    pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
    pdf.text(heroCopy, margin + 6, heroCopyY);
    pdf.setFont(BRAND_FONT, 'normal');
    const proofX = margin + contentWidth - 63;
    const proofY = yPosition + 18;
    drawRoundedCard(pdf, proofX, proofY, 57, 34, COLORS.paper, COLORS.border, 4);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(6.5);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text('RELEASE POSITION', proofX + 4, proofY + 6);
    pdf.setFontSize(14);
    pdf.setTextColor(getSeverityColor(scan.riskLevel)[0], getSeverityColor(scan.riskLevel)[1], getSeverityColor(scan.riskLevel)[2]);
    pdf.text(`${scan.riskScore}/100`, proofX + 4, proofY + 15);
    pdf.setFontSize(8.5);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.text(scan.riskLevel, proofX + 27, proofY + 15);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(6.6);
    pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
    const proofLines = pdf.splitTextToSize(releasePositioning, 42);
    pdf.text(proofLines, proofX + 4, proofY + 22);
    yPosition += heroPanelHeight + 8;
    const topMetricWidth = (contentWidth - 9) / 4;
    drawMetricCard(pdf, margin, yPosition, topMetricWidth, 22, 'Findings', String(scan.findings.length), 'open items');
    drawMetricCard(pdf, margin + topMetricWidth + 3, yPosition, topMetricWidth, 22, 'Critical', String(findingsBySeverity.critical), 'highest urgency', COLORS.critical);
    drawMetricCard(pdf, margin + (topMetricWidth + 3) * 2, yPosition, topMetricWidth, 22, 'High', String(findingsBySeverity.high), 'priority review', COLORS.high);
    drawMetricCard(pdf, margin + (topMetricWidth + 3) * 3, yPosition, topMetricWidth, 22, 'Artifact Size', `${(scan.fileSize / 1024).toFixed(1)} KB`, 'payload reviewed');
    yPosition += 30;
    const leftColumnWidth = 84;
    const nextMoveCardHeight = 60;
    drawRoundedCard(pdf, margin, yPosition, leftColumnWidth, nextMoveCardHeight, COLORS.paper, COLORS.border, 5);
    drawSectionHeading(pdf, margin + 5, yPosition + 8, 'Hero Proof Panel', 'Why this report matters');
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7.6);
    pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
    const proofBody = pdf.splitTextToSize('This report connects the artifact scanned, the risk surfaced, and the evidence needed for release review. It is designed to help engineering, security, and customer stakeholders align quickly.', leftColumnWidth - 10);
    pdf.text(proofBody, margin + 5, yPosition + 24);
    drawRoundedCard(pdf, margin + leftColumnWidth + 4, yPosition, contentWidth - leftColumnWidth - 4, nextMoveCardHeight, COLORS.cyanSoft, COLORS.border, 5);
    drawSectionHeading(pdf, margin + leftColumnWidth + 9, yPosition + 8, 'Recommended Next Move', 'Action framing for this release');
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(9.2);
    pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
    const actionWidth = contentWidth - leftColumnWidth - 22;
    const actionLines = pdf.splitTextToSize(primaryAction, actionWidth);
    pdf.text(actionLines, margin + leftColumnWidth + 9, yPosition + 25);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7.1);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    const actionSupportLines = pdf.splitTextToSize('Use the validate link and exported evidence to support release, remediation, or customer handoff.', actionWidth);
    pdf.text(actionSupportLines, margin + leftColumnWidth + 9, yPosition + 39);
    yPosition += nextMoveCardHeight + 8;
    const reviewCardHeight = 36;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, reviewCardHeight, COLORS.emeraldSoft, COLORS.border, 5);
    drawSectionHeading(pdf, margin + 5, yPosition + 7, 'Review Journey', 'A more intentional path from intake to evidence');
    const steps = ['Intake Verified', 'Findings Reviewed', 'Fixes Prioritized', 'Evidence Shared'];
    const stepGap = 4;
    const stepsLeft = margin + 5;
    const stepsInnerWidth = contentWidth - 10;
    const stepWidth = (stepsInnerWidth - stepGap * (steps.length - 1)) / steps.length;
    steps.forEach((step, index) => {
        const stepX = stepsLeft + index * (stepWidth + stepGap);
        const stepY = yPosition + 20;
        drawRoundedCard(pdf, stepX, stepY, stepWidth, 9.5, COLORS.paper, COLORS.border, 3);
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(5.8);
        pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
        const stepText = pdf.splitTextToSize(step, stepWidth - 4);
        pdf.text(stepText, stepX + 2, stepY + (stepText.length > 1 ? 3.8 : 5.8));
        if (index < steps.length - 1) {
            pdf.setDrawColor(COLORS.emerald[0], COLORS.emerald[1], COLORS.emerald[2]);
            pdf.setLineWidth(0.5);
            pdf.line(stepX + stepWidth + 0.8, stepY + 4.7, stepX + stepWidth + stepGap - 0.8, stepY + 4.7);
        }
    });
    addNewPage();
    drawSectionHeading(pdf, margin, yPosition, 'Executive Summary', 'Risk posture and signoff guidance', 'This page is designed to make the audit persuasive at a glance: what was scanned, what needs attention, and what the team should do next.');
    yPosition += 26;
    const detailCardWidth = (contentWidth - 4) / 2;
    drawRoundedCard(pdf, margin, yPosition, detailCardWidth, 30, COLORS.paper, COLORS.border, 5);
    drawRoundedCard(pdf, margin + detailCardWidth + 4, yPosition, detailCardWidth, 30, COLORS.paper, COLORS.border, 5);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(7);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text('AUDIT DETAILS', margin + 4, yPosition + 6);
    pdf.text('VALIDATION + OWNERSHIP', margin + detailCardWidth + 8, yPosition + 6);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
    const auditRows = [
        ['Artifact', scan.fileName],
        ['Scanned', formatDateTime(scan.scannedAt)],
        ['Payload', `${(scan.fileSize / 1024).toFixed(2)} KB`],
    ];
    auditRows.forEach(([label, value], index) => {
        const rowY = yPosition + 12 + index * 5.5;
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.text(`${label}:`, margin + 4, rowY);
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.text(pdf.splitTextToSize(value, detailCardWidth - 24), margin + 24, rowY);
    });
    const validationRows = [
        ['Owner', ownerIdentity],
        ['Fingerprint', scan.fileHashFormatted],
    ];
    validationRows.forEach(([label, value], index) => {
        const rowY = yPosition + 12 + index * 8.5;
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.text(`${label}:`, margin + detailCardWidth + 8, rowY);
        pdf.setFont(BRAND_FONT, 'normal');
        const lines = pdf.splitTextToSize(value, detailCardWidth - 28);
        pdf.text(lines, margin + detailCardWidth + 28, rowY);
    });
    yPosition += 38;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, 24, getSeveritySoftColor(scan.riskLevel), COLORS.border, 5);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text('CTA FRAMING', margin + 5, yPosition + 6);
    pdf.setFontSize(12);
    pdf.setTextColor(getSeverityColor(scan.riskLevel)[0], getSeverityColor(scan.riskLevel)[1], getSeverityColor(scan.riskLevel)[2]);
    pdf.text(primaryAction, margin + 5, yPosition + 14);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
    pdf.text('If this report is being reviewed externally, use the QR code on each page to validate report authenticity.', margin + 5, yPosition + 20);
    yPosition += 32;
    drawSectionHeading(pdf, margin, yPosition, 'Finding Distribution', 'Severity mix across the artifact');
    yPosition += 16;
    const severityTypes = [
        { name: 'Critical', color: COLORS.critical, count: findingsBySeverity.critical },
        { name: 'High', color: COLORS.high, count: findingsBySeverity.high },
        { name: 'Medium', color: COLORS.medium, count: findingsBySeverity.medium },
        { name: 'Low', color: COLORS.low, count: findingsBySeverity.low },
        { name: 'Info', color: COLORS.info, count: findingsBySeverity.info },
    ];
    severityTypes.forEach((severity) => {
        const percentage = scan.findings.length > 0 ? (severity.count / scan.findings.length) * 100 : 0;
        drawRoundedCard(pdf, margin, yPosition - 4.5, contentWidth, 9, COLORS.paper, COLORS.border, 3);
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(severity.color[0], severity.color[1], severity.color[2]);
        pdf.text(`${severity.name}`, margin + 4, yPosition + 1);
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
        pdf.text(`${severity.count} findings`, margin + 24, yPosition + 1);
        drawProgressBar(pdf, margin + 52, yPosition - 1.7, 85, 3.6, percentage, severity.color);
        pdf.setFontSize(7);
        pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
        pdf.text(`${percentage.toFixed(0)}%`, margin + 141, yPosition + 1);
        yPosition += 11;
    });
    if (scan.findings.length === 0) {
        yPosition += 2;
        drawRoundedCard(pdf, margin, yPosition, contentWidth, 28, COLORS.emeraldSoft, [167, 243, 208], 5);
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(8);
        pdf.setTextColor(COLORS.emerald[0], COLORS.emerald[1], COLORS.emerald[2]);
        pdf.text('FIX OUTCOME', margin + 5, yPosition + 6);
        pdf.setFontSize(13);
        pdf.text('Zero findings remain after the current scan.', margin + 5, yPosition + 14);
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setFontSize(7.5);
        pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
        const zeroOutcomeLines = pdf.splitTextToSize('This asset is positioned for evidence-backed release review. Keep this report with the validation link as a clean handoff artifact for engineering, security, and customer stakeholders.', contentWidth - 10);
        pdf.text(zeroOutcomeLines, margin + 5, yPosition + 20);
        yPosition += 36;
    }
    if (scan.findings.length > 0) {
        addNewPage();
        drawSectionHeading(pdf, margin, yPosition, 'Detailed Findings', 'Security issues that shape the release decision', 'Each finding below includes severity, impact, location, and a remediation path so the team can move from triage to action without losing context.');
        yPosition += 24;
        scan.findings.forEach((finding, index) => {
            const descWidth = contentWidth - 14;
            const detailPanelWidth = contentWidth - 34;
            const descLines = pdf.splitTextToSize(finding.description || 'No description provided.', descWidth);
            const impactLines = finding.impact ? pdf.splitTextToSize(finding.impact, detailPanelWidth) : [];
            const remediationLines = pdf.splitTextToSize(finding.remediation || 'Remediation guidance unavailable.', detailPanelWidth);
            const titleLines = pdf.splitTextToSize(`${index + 1}. ${finding.title}`, contentWidth - 40);
            const metaLines = pdf.splitTextToSize(`CVSS ${finding.cvss || 'N/A'} · ${finding.category || 'General'} · ${finding.location || 'Unknown location'} · CWE ${finding.cwe || 'N/A'}`, contentWidth - 40);
            const cardHeight = 22 +
                titleLines.length * 4.4 +
                metaLines.length * 3.4 +
                descLines.length * 3.8 +
                (impactLines.length ? 10 + impactLines.length * 3.6 : 0) +
                10 + remediationLines.length * 3.6;
            checkPageSpace(cardHeight + 8);
            const severityColor = getSeverityColor(finding.severity);
            const severitySoft = getSeveritySoftColor(finding.severity);
            drawRoundedCard(pdf, margin, yPosition, contentWidth, cardHeight, COLORS.paper, COLORS.border, 5);
            drawRoundedCard(pdf, margin + 4, yPosition + 4, 26, 8, severitySoft, severityColor, 3);
            pdf.setFont(BRAND_FONT, 'bold');
            pdf.setFontSize(7);
            pdf.setTextColor(severityColor[0], severityColor[1], severityColor[2]);
            pdf.text(finding.severity, margin + 8, yPosition + 9);
            pdf.setFont(BRAND_FONT, 'bold');
            pdf.setFontSize(11);
            pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
            pdf.text(titleLines, margin + 34, yPosition + 9);
            pdf.setFont(BRAND_FONT, 'normal');
            pdf.setFontSize(7.5);
            pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
            pdf.text(metaLines, margin + 34, yPosition + 13 + titleLines.length * 3.9);
            let cardY = yPosition + 16 + titleLines.length * 4.2 + metaLines.length * 3.6;
            pdf.setFont(BRAND_FONT, 'normal');
            pdf.setFontSize(8.2);
            pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
            pdf.text(descLines, margin + 4, cardY);
            cardY += descLines.length * 3.8 + 2;
            if (impactLines.length) {
                drawRoundedCard(pdf, margin + 4, cardY - 1.5, contentWidth - 8, 7 + impactLines.length * 3.6, COLORS.criticalSoft, [254, 202, 202], 3);
                pdf.setFont(BRAND_FONT, 'bold');
                pdf.setFontSize(7);
                pdf.setTextColor(COLORS.critical[0], COLORS.critical[1], COLORS.critical[2]);
                pdf.text('Impact', margin + 7, cardY + 2);
                pdf.setFont(BRAND_FONT, 'normal');
                pdf.setFontSize(7.5);
                pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
                pdf.text(impactLines, margin + 18, cardY + 2);
                cardY += 9 + impactLines.length * 3.6;
            }
            drawRoundedCard(pdf, margin + 4, cardY - 1.5, contentWidth - 8, 7 + remediationLines.length * 3.6, COLORS.emeraldSoft, [167, 243, 208], 3);
            pdf.setFont(BRAND_FONT, 'bold');
            pdf.setFontSize(7);
            pdf.setTextColor(COLORS.emerald[0], COLORS.emerald[1], COLORS.emerald[2]);
            pdf.text('Remediation', margin + 7, cardY + 2);
            pdf.setFont(BRAND_FONT, 'normal');
            pdf.setFontSize(7.5);
            pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
            pdf.text(remediationLines, margin + 26, cardY + 2);
            yPosition += cardHeight + 6;
        });
    }
    addNewPage();
    drawSectionHeading(pdf, margin, yPosition, 'Compliance Matrix', 'Framework posture at a glance', 'Use this page to communicate where the scanned artifact aligns, where it does not, and where further review may be needed.');
    yPosition += 22;
    const statusColumnWidth = 32;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, 9, COLORS.brand, COLORS.brand, 4);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(255, 255, 255);
    pdf.text('Framework', margin + 4, yPosition + 5.7);
    pdf.text('Status', margin + contentWidth - statusColumnWidth + 5, yPosition + 5.7);
    yPosition += 11;
    const complianceEntries = Object.entries(scan.compliance);
    complianceEntries.forEach(([standard, status], idx) => {
        checkPageSpace(10);
        const displayName = standard
            .replace(/_/g, ' ')
            .split(' ')
            .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
            .join(' ');
        drawRoundedCard(pdf, margin, yPosition - 1, contentWidth, 8.5, idx % 2 === 0 ? COLORS.paper : COLORS.panel, COLORS.border, 3);
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setFontSize(8);
        pdf.setTextColor(COLORS.text[0], COLORS.text[1], COLORS.text[2]);
        pdf.text(pdf.splitTextToSize(displayName, contentWidth - statusColumnWidth - 12), margin + 4, yPosition + 4);
        const statusColor = status === 'COMPLIANT' ? COLORS.emerald : status === 'NON_COMPLIANT' ? COLORS.critical : COLORS.high;
        const statusBg = status === 'COMPLIANT' ? COLORS.emeraldSoft : status === 'NON_COMPLIANT' ? COLORS.criticalSoft : COLORS.highSoft;
        drawRoundedCard(pdf, margin + contentWidth - statusColumnWidth, yPosition + 0.6, 26, 5.5, statusBg, statusColor, 2.5);
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(6.2);
        pdf.setTextColor(statusColor[0], statusColor[1], statusColor[2]);
        pdf.text(String(status), margin + contentWidth - statusColumnWidth + 2.5, yPosition + 4.1);
        yPosition += 9.5;
    });
    yPosition += 4;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, 16, COLORS.panel, COLORS.border, 4);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
    pdf.text('Closing note', margin + 4, yPosition + 6);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text('Use recurring scans to compare new releases, preserve evidence, and track the path to zero residual vulnerabilities over time.', margin + 4, yPosition + 11);
    yPosition += 24;
    drawRoundedCard(pdf, margin, yPosition, contentWidth, 30, COLORS.paper, COLORS.border, 5);
    pdf.setFont(BRAND_FONT, 'bold');
    pdf.setFontSize(8);
    pdf.setTextColor(COLORS.brand[0], COLORS.brand[1], COLORS.brand[2]);
    pdf.text('Executive Handoff', margin + 4, yPosition + 6);
    pdf.setFont(BRAND_FONT, 'normal');
    pdf.setFontSize(7.5);
    pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
    pdf.text('Use this block for security signoff, customer delivery, or internal release approval.', margin + 4, yPosition + 11);
    const signatureTop = yPosition + 19;
    const signatureWidth = (contentWidth - 8) / 3;
    [
        'Prepared by',
        'Reviewed by',
        'Release decision',
    ].forEach((label, index) => {
        const x = margin + index * (signatureWidth + 4);
        pdf.setDrawColor(COLORS.border[0], COLORS.border[1], COLORS.border[2]);
        pdf.setLineWidth(0.35);
        pdf.line(x + 2, signatureTop, x + signatureWidth - 2, signatureTop);
        pdf.setFont(BRAND_FONT, 'bold');
        pdf.setFontSize(7);
        pdf.setTextColor(COLORS.slate[0], COLORS.slate[1], COLORS.slate[2]);
        pdf.text(label, x + 2, signatureTop + 5);
        pdf.setFont(BRAND_FONT, 'normal');
        pdf.setFontSize(6.5);
        pdf.setTextColor(COLORS.muted[0], COLORS.muted[1], COLORS.muted[2]);
        pdf.text(index === 2 ? 'Approved / Hold / Follow-up' : 'Name and date', x + 2, signatureTop + 9);
    });
    pdf.save(`SnapV_Report_${scan.fileHash.substring(0, 8)}.pdf`);
}
